<script setup lang="ts">
import { ref, computed, nextTick, onMounted, watch } from "vue";
import { api } from "../../../api/Api";
import Toast from "../../../services/Toast";
import {
  Currency,
  Customer,
  Item,
  ItemPackingPrice,
  Outlet,
  WarehouseShelf,
} from "../../../types/models";
import { formatDate, formatNumber, isEmpty } from "../../../utils/CommonFunctions";
import IconComponent from "../common/IconComponent.vue";
import ItemGridSelectItems from "./ItemGridSelectItems.vue";
import { useScreenSize } from "../../../utils/useScreenSize";
import ItemSearchMobile from "./ItemSearchMobile.vue";
import ItemSelectGrid from "./ItemSelectGrid.vue";
import TabComponent from "../form/TabComponent.vue";

const props = defineProps<{
  items?: Item[] | [];
  readonly: boolean;
  warehouses_id?: number | string | null | undefined;
  outlet?: Outlet | null
  exchange_rate: number;
  currency?: Currency;
  price_lists_id?: number | string;
  allowNewRow: boolean;
  allowChangePrice?: boolean;
  allowDiscount?: boolean;
  ignoreBatchSerial?: boolean;
  shelfManaged?: boolean;
  shelfs?: WarehouseShelf[] | [];
  customer?: Customer | "";
}>();

const emit = defineEmits<{
  (e: "save"): void;
  (e: "items", items: Item[]): void;
}>();

const itemList = ref<Item[]>([]);
const { isMobile } = useScreenSize();
const taxDetails = ref([]);

/*const handleTax = (item: Item) => {
  const tax: TaxDefinition = taxDetails.value.find(
    (e: TaxDefinition) => e.id == item.tax_details_id
  ) || { id: "", name: "", percentage: 0 };
  item.tax_rate = tax ? tax.percentage : 0;
  updateLineTotal(item);
};*/

function addItemFromMobile(item?: Item) {
  if (item) itemList.value.push(item);
  selectedItem.value = itemList.value[itemList.value.length-1]
  setItem(selectedItem.value)
  mobileEditRowIndex.value = -1
}

function addItemRow(index: number) {
  if (props.readonly || !props.allowNewRow || isMobile.value) return;
  if (index < 0) {
    index = itemList.value.length - 1;
  }

  if (
    itemList.value.length > 0 &&
    isEmpty(itemList.value[itemList.value.length - 1].name)
  ) {
    focusNewRow();
    return;
  }

  const newItem: Item = {
    id: itemList.value.length,
    items_id: "",
    photo: "",
    code: "",
    name: "",
    item_name: "",
    sales_name: "",
    item_types_id: "",
    flexi_price: false,
    ignore_discount: false,
    tracked_by: "Quantity",
    purchase_uom: "",
    purchase_uom_qty: 1,
    sales_uom: "",
    sales_uom_qty: 1,
    is_package: false,
    use_package_price: false,
    is_expiry: false,
    tax_details_id: "",
    inventory_uom_id: "",
    purchase_uom_id: "",
    sales_uom_id: "",
    has_variants: false,
    variants: "",
    tax: "",
    tax_rate: 0,
    purchase_order_items_id: "",
    purchase_goods_receipts_id: "",
    warehouses_id: props.warehouses_id ?? null,
    inventory_uom_qty: 0,
    item_variants_id: "",
    unit_of_measures_id: "",
    unit_of_measure: "",
    on_hand: 0,
    quantity: 1,
    uom_qty: 1,
    inventory_qty: 1,
    cost: 0,
    original_price: 0,
    price: 0,
    selling_price: 0,
    sub_total: 0,
    discount_p: 0,
    discount_a: 0,
    value_before_tax: 0,
    tax_amount: 0,
    net_total: 0,
    remarks: "",
    uid: "",
    batches: [],
    serials: [],
    packing_prices: [],
    quantity_prices: [],
    shelfs: [],
    default_sales_uom_id: "",
    default_sales_uom_qty: 0
  };
  itemList.value.push(newItem);
  focusNewRow();
}

const focusNewRow = () => {
  nextTick(() => {
    const index = itemList.value.length - 1;
    const input = document.getElementById("code_" + index);
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
};

// Computed Properties

const discount = computed(() => {
  return itemList.value.reduce((sum: number, item: Item) => {
    return sum + (item.discount_a ?? 0);
  }, 0);
});

const subtotal = computed(() => {
  return itemList.value.reduce((sum: number, item: Item) => {
    return sum + (item.sub_total ?? 0);
  }, 0);
});

const service_charge = computed(() => {
  return (subtotal.value - discount.value) * (props.outlet?.service_charge ?? 0) / 100
});


const value_before_tax = computed(() => {
  return (subtotal.value - discount.value) + service_charge.value;
});

const taxAmount = computed(() => {
  const item_tax = itemList.value.reduce((sum: number, item: Item) => {
    return sum + (item.tax_amount ?? 0);
  }, 0)
  if ((props.outlet?.service_charge_tax_rate ?? 0) > 0) {
    return item_tax + (service_charge.value * (props.outlet?.service_charge_tax_rate ?? 0) / 100)
  }
  return item_tax;
});

const total = computed(() => {
  return value_before_tax.value + taxAmount.value;
});

const showItemsDialog = ref(false);
const mobileEditRowIndex = ref(-1)

function updateLineTotal(item: Item) {
  item.inventory_qty = (item.quantity || 1) * (item.sales_uom_qty || 1);
  item.sub_total = (item.quantity || 1) * (item.price || 0);
  item.discount_a = Math.round((item.sub_total * item.discount_p || 0) / 100);
  item.value_before_tax = item.sub_total - item.discount_a;
  item.tax_amount =
    parseFloat(
      (((item.sub_total - item.discount_a) * (item.tax_rate || 0)) / 100).toFixed(2)
    );
  item.net_total = parseFloat((item.sub_total - item.discount_a + item.tax_amount).toFixed(2));

  item.value_before_tax = item.net_total - item.tax_amount
  /*let rounding = tokenManager.getSetting('sales_documents_rounding')
  if (rounding === 'Round Up to 0 decimals' && item.net_total > 0) {
    item.net_total = Math.ceil(item.net_total)
    item.price = item.net_total/item.quantity
  } else if (rounding == 'Round Down to 0 decimals' && item.net_total > 0) {
    item.net_total = Math.floor(item.net_total)
    item.price = item.net_total/item.quantity
  } else if (rounding == 'Round to 0 decimals' && item.net_total > 0) {
    item.net_total = Math.round(item.net_total)
    item.price = item.net_total/item.quantity
  }*/

  emit("items", itemList.value);
}

function updateDiscount(item: Item) {
  item.discount_p = parseFloat((((item.discount_a ?? 0) / (item.sub_total || 1)) * 100).toFixed(2));
  updateLineTotal(item);
}

const selectedItemIndex = ref(-1);
const selectItems = ref<Item[] | []>([]);
const selectedItem = ref<Item>();
async function handleItemCodeEnter(item: Item, index: number) {
  selectedItemIndex.value = index;
  selectedItem.value = item;
  if (isEmpty(item.code)) return;
  const params = {
    search_query: item.code,
    price_lists_id: props.price_lists_id,
    warehouses_id: props.warehouses_id,
  };
  const response = await api.post(
    "inventory/item-management/items/search",
    params
  );
  if (response.success == true) {
    if (response.data.length == 1) {
      setItem(response.data[0]);
    } else if (response.data.length > 1) {
      selectItems.value = response.data;
      showItemsDialog.value = true;
    } else {
      Toast.show("Item not found", "warning");
    }
  } else {
    Toast.show("Item not found", "warning");
  }
}

async function getTaxList() {
  const response = await api.post("finance/master-data/tax-definitions/list", null,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        skipLoading: true,
      });
  if (response.success == true) {
    taxDetails.value = response.data;
  }
}

const setItem = (result: any, copy: boolean = false) => {
  const item = selectedItem.value;
  console.log(item)
  if (item) {
    item.items_id = result.id;
    item.photo = result.photo;
    item.code = result.code;
    item.name = result.name;
    item.item = result.item;
    item.sales_name = result.sales_name;
    item.item_types_id = result.item_types_id;
    item.purchase_uom = result.purchase_uom;
    item.purchase_uom_qty = result.purchase_uom_qty;
    item.sales_uom = result.sales_uom;
    item.sales_uom_qty = result.sales_uom_qty;
    item.default_sales_uom_qty = result.sales_uom_qty;
    item.is_package = result.is_package;
    item.use_package_price = result.use_package_price;
    item.is_expiry = result.is_expiry;
    item.tax_details_id = result.tax_details_id;
    item.inventory_uom_id = result.inventory_uom_id;
    item.inventory_uom_qty = result.inventory_uom_qty;
    item.purchase_uom_id = result.purchase_uom_id;
    item.sales_uom_id = result.sales_uom_id;
    item.default_sales_uom_id = result.sales_uom_id;
    item.has_variants = result.has_variants;
    item.variants = result.variants;
    item.tax = result.tax;
    item.tax_rate = result.tax_rate;
    item.uid = result.uid;
    item.tracked_by = result.tracked_by;

    item.warehouses_id = result.warehouses_id ?? props.warehouses_id;
    item.price_lists_id = result.price_lists_id ?? props.price_lists_id;

    //item.purchase_order_items_id = result.purchase_order_items_id;
    //item.purchase_goods_receipts_id = result.purchase_goods_receipts_id;
    //item.warehouses_id = result.warehouses_id;
    item.item_variants_id = result.item_variants_id;
    item.unit_of_measures_id = result.unit_of_measures_id;
    item.unit_of_measure = result.unit_of_measure;
    item.on_hand = result.on_hand;
    item.original_on_hand = result.on_hand;
    item.quantity = result.quantity ?? 1;
    item.uom_qty = result.sales_uom_qty;
    item.inventory_qty = result.inventory_qty ?? 1;
    item.cost = result.cost;
    item.original_price = result.selling_price;
    item.price = result.price;
    item.selling_price = result.selling_price;
    item.sub_total = 0;
    item.discount_p = 0;
    item.discount_a = 0;
    item.value_before_tax = 0;
    item.tax_amount = result.tax_rate;
    item.remarks = "";
    item.remarks = "";
    if (!copy) {
      item.batches = result.batches;
      item.serials = result.serials;
    }
    item.packing_prices = result.packing_prices;
    item.quantity_prices = result.quantity_prices;

    updateLineTotal(item);

    if (
      (item.tracked_by == "Batch" ||
        item.tracked_by == "Serial" ||
        props.shelfManaged) &&
      !props.ignoreBatchSerial
    ) {
      if (item.tracked_by == 'Batch') {
        activeTab.value = 'batch'
        
        if (result.shelfs) {
          item.batches?.forEach(b => {
            b.shelfs = result.shelfs.filter((e: any) => e.warehouses_id == props.warehouses_id && e.item_variants_id == item.item_variants_id)
          });          
        }
      } else if (item.tracked_by == 'Serial') {
        activeTab.value = 'serial'
      } else if (item.shelfs && item.shelfs.length > 0) {
        activeTab.value = 'shelfs'
        if (result.shelfs) {
          item.shelfs = result.shelfs.filter((e: any) => e.warehouses_id == props.warehouses_id && e.item_variants_id == item.item_variants_id)
        }
      }
      expandedRows.value.add(String(item.id));
    }

    console.log(item)

    showItemsDialog.value = false;
    // Focus the quantity input
    nextTick(() => {
      const input = document.getElementById(
        "quantity_" + selectedItemIndex.value
      );
      if (input) {
        input.focus();
        (input as HTMLInputElement).select();
      }
    });
  }
};

const removeItem = (index: number) => {
  itemList.value.splice(index, 1)
}

function handleQuantityEnter(item: Item, index: number) {
  item.price = item.original_price;
  if ((item.batches?.length ?? 0) == 1 && !props.ignoreBatchSerial) {
    if (item.tracked_by == "Batch" && item.batches && item.batches.length > 0) {
      item.batches[0].quantity = item.inventory_qty ?? 0;
    }
  }
  nextTick(() => {
    var input = null;
    if (props.allowChangePrice) {
      input = document.getElementById("price_" + index);
    } else if (!props.ignoreBatchSerial) {
      if (
        itemList.value[index].tracked_by == "Batch" &&
        (item.batches?.length ?? 0) > 1
      ) {
        input = document.getElementById(
          "batch_quantity_" + item.items_id + "_" + 0
        );
      } else if (itemList.value[index].tracked_by == "Serial") {
        input = document.getElementById(
          "serial_quantity_" + item.items_id + "_" + 0
        );
      } else if (props.shelfManaged) {
        input = document.getElementById(
          "shelf_quantity_" + item.items_id + "_" + 0
        );
      } else {
        addItemRow(index);
      }
    } else {
      addItemRow(index);
    }
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
}

function handleUnitPriceEnter(index: number) {
  updateLineTotal(itemList.value[index]);
  if (index === itemList.value.length - 1) {
    addItemRow(index);
  } else {
    index = itemList.value.length - 1;
    nextTick(() => {
      const input = document.getElementById("code_" + index);
      if (input) {
        input.focus();
        (input as HTMLInputElement).select();
      }
    });
  }
}

const getShelfSum = (item: Item): number => {
  return (
    item.shelfs?.reduce((sum: number, shelf: WarehouseShelf) => {
      const quantity = Number(shelf.quantity);
      if (!isNaN(quantity)) {
        return sum + quantity;
      }
      return sum;
    }, 0) ?? 0
  );
};

const getBatchSum = (item: Item): number => {
  return (
    item.batches?.reduce((sum: number, batch: { quantity: number }) => {
      if (typeof batch.quantity === "number") {
        return sum + batch.quantity;
      }
      return sum;
    }, 0) ?? 0
  );
};

const getSerialSum = (item: Item): number => {
  return (
    item.serials?.reduce((sum: number, serial: { quantity: number }) => {
      if (typeof serial.quantity === "number") {
        return sum + serial.quantity;
      }
      return sum;
    }, 0) ?? 0
  );
};

function handleShelfQuantityEnter(item: Item, batchIndex: number) {
  const qty = getShelfSum(item);
  if (qty == item.inventory_qty) {
    addItemRow(itemList.value.length - 1);
  } else {
    nextTick(() => {
      const input = document.getElementById(
        "shelf_quantity_" + item.items_id + "_" + (batchIndex + 1)
      );
      if (input) {
        input.focus();
        (input as HTMLInputElement).select();
      }
    });
  }
}

function handleBatchQuantityEnter(item: Item, batchIndex: number) {
  const qty = getBatchSum(item);
  if (qty == item.inventory_qty) {
    addItemRow(itemList.value.length - 1);
  } else {
    nextTick(() => {
      const input = document.getElementById(
        "batch_quantity_" + item.items_id + "_" + (batchIndex + 1)
      );
      if (input) {
        input.focus();
        (input as HTMLInputElement).select();
      }
    });
  }
}

function handleSerialQuantityEnter(item: Item, serialIndex: number) {
  const qty = getSerialSum(item);
  if (qty == item.inventory_qty) {
    addItemRow(itemList.value.length - 1);
  } else {
    nextTick(() => {
      const input = document.getElementById(
        "serial_quantity_" + item.items_id + "_" + (serialIndex + 1)
      );
      if (input) {
        input.focus();
        (input as HTMLInputElement).select();
      }
    });
  }
}

const packingEditRowIndex = ref(-1);

const updatePackingPrice = (item: Item, packing: ItemPackingPrice) => {
  item.sales_uom_id = packing.unit_of_measures_id;
  item.sales_uom = packing.unit_of_measure;
  item.sales_uom_qty = packing.inventory_qty;
  console.log(item.original_on_hand, packing.inventory_qty, Math.floor((item.original_on_hand ?? 0) / packing.inventory_qty) )
  item.on_hand = item.original_on_hand ? Math.floor(item.original_on_hand / packing.inventory_qty) : 0;
  item.price = packing.selling_price;
  console.log(item)
  packingEditRowIndex.value = -1;
  updateLineTotal(item);
};

const clearPackingPrice = (item: Item) => {
  item.sales_uom_id = item.default_sales_uom_id;
  item.sales_uom = item.unit_of_measure;
  item.sales_uom_qty = item.default_sales_uom_qty;
  item.price = item.original_price;
  item.on_hand = item.original_on_hand ?? 0;
  packingEditRowIndex.value = -1;
  updateLineTotal(item);
};

// Add expanded items tracking
const expandedRows = ref<Set<string>>(new Set());

function toggleItemExpand(item: Item) {
  if (expandedRows.value.has(String(item.id))) {
    expandedRows.value.delete(String(item.id));
  } else {
    expandedRows.value.add(String(item.id));
  }
}

function isExpanded(item: Item): boolean {
  return expandedRows.value.has(String(item.id));
}

const showItemGridList = ref(false)
const handleSelectedItems = async (items: Item[]) => {
  items.forEach(async item => {
    let hasItem = itemList.value.find((e) => e.code == item.code)
    if (hasItem) {
      selectedItem.value = hasItem
      setItem(item, true)
    } else {
      addItemFromMobile(item)
    }
  });
}

const closeItemSelect = () => {
  showItemsDialog.value = false;
  selectItems.value = [];
};

watch(
  () => props.items,
  (newValue) => {
    if (newValue) {
      itemList.value = props.items ?? [];
      itemList.value.forEach((item) => {
       updateLineTotal(item);
      });
    }
  },
  { immediate: true }
);

watch(
  () => props.allowNewRow,
  (newValue) => {
    if (newValue == true) {
      addItemRow(-1);
    }
  },
  { immediate: true }
);

const activeTab = ref("shelfs");

defineExpose({ addItemRow, handleSelectedItems });

onMounted(() => {
  itemList.value = props.items ?? [];
  getTaxList();
});
</script>

<template>
  <div class="mb-8">
    <div
      v-if="!isMobile"
      class="relative overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg"
    >
    <div class="flex justify-between items-center mb-4" v-if="!readonly && allowNewRow">
      <div></div>
      <div>
      <button
        @click="showItemGridList = true"
        
        class="px-4 py-2 mr-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-blue-700"
      >
        Item Grid
      </button>
      <button
        @click="addItemRow(-1)"
        v-if="!readonly"
        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
      >
        Add Item Row
      </button>
    </div>
    </div>
      <div class="overflow-x-auto overflow-y-auto max-h-[500px]">
        <table class="w-full">
          <thead class="bg-white dark:bg-gray-900">
            <tr>
              <!-- Fixed left column -->
              <th
                class="sticky left-0 z-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Code
              </th>

              <!-- Scrollable columns -->
              <!--<th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Photo</th>-->
              <th
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Name
              </th>
              <!--<th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Item Type</th>-->
              <th
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Packing
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                On Hand
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Quantity
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Price
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Sub Total
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Discount %
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Discount Amount
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Value Before Tax
              </th>
              <!--<th
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Tax Basis
              </th>-->
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Tax Amount
              </th>

              <!-- Fixed right column -->
              <th
                class="sticky right-0 bg-white dark:bg-gray-900 z-20 px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Net Total
              </th>
              <!--<th class="sticky right-0 z-20 px-4 py-3"></th>-->
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <template v-for="(item, index) in itemList" :key="item.id">
              <tr>
                <!-- Fixed left column -->
                <td class="sticky left-0 z-10 px-4 py-3 w-[200px] min-w-60">
                  <div class="flex items-center">
                    <button
                      @click="toggleItemExpand(item)"
                      class="mr-2"
                      v-if="
                        (item.batches?.length ?? 0) > 0 ||
                        (item.serials?.length ?? 0) > 0
                      "
                    >
                      <IconComponent
                        :name="isExpanded(item) ? 'ArrowUp' : 'ArrowDown'"
                      />
                    </button>
                    <div class="relative" v-if="!props.readonly">
                      <input
                        :id="'code_' + index"
                        v-model="item.code"
                        type="text"
                        class="w-full pl-3 pr-10 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
                        @keyup.enter="handleItemCodeEnter(item, index)"
                      />
                      <button
                        @click="showItemsDialog = true"
                        class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Search items"
                      >
                        🔍
                      </button>
                    </div>
                    <span v-else>{{ item.code }}</span>
                  </div>
                </td>

                <!-- Scrollable columns -->
                <!--<td class="px-4 py-3">
                      <img 
                        v-if="item.photo" 
                        :src="item.photo" 
                        class="w-10 h-10 object-cover rounded"
                        alt=""
                      />
                    </td>-->
                <td class="px-4 py-3 min-w-80">{{ item.name ?? item.item }}</td>
                <td class="px-4 py-3 relative group min-w-40 align-middle">
                  <div
                  v-if="packingEditRowIndex == index"
                  class="right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20"
                  >
                  <button
                    v-for="p in item.packing_prices?.filter(
                    (e) => e.price_lists_id == props.price_lists_id
                    )"
                    @click="updatePackingPrice(item, p)"
                    :key="p.unit_of_measures_id"
                    class="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    {{ p.unit_of_measure }} @
                    {{ formatNumber(p.selling_price / props.exchange_rate) }}
                  </button>
                  </div>
                  <span class="align-middle">
                  {{ item.sales_uom }} x
                  {{ formatNumber(item.sales_uom_qty, 2, 0) }} pcs
                  </span>
                  <button
                  v-if="(item.packing_prices?.length ?? 0) > 0 && packingEditRowIndex != index && !readonly && item.default_sales_uom_id == item.sales_uom_id"
                  @click="packingEditRowIndex = index"
                  class="absolute bottom-3 right-1 p-1 text-xs text-blue-600 dark:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 align-middle"
                  title="Change Price"
                  >
                  <IconComponent name="PackageSearch" />
                  </button>
                  <button
                  v-if="
                    packingEditRowIndex != index &&
                    !readonly &&
                    item.sales_uom_id != item.default_sales_uom_id
                  "
                  @click="clearPackingPrice(item)"
                  class="absolute bottom-3 right-1 p-1 text-xs text-blue-600 dark:text-blue-400 hover:text-red-700 dark:hover:text-red-300 align-middle"
                  title="Clear"
                  >
                  <IconComponent name="Trash2" />
                  </button>
                  <button
                  v-if="packingEditRowIndex == index && !readonly"
                  @click="packingEditRowIndex = -1"
                  class="absolute bottom-3 right-1 p-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 align-middle"
                  title="Cancel"
                  >
                  <IconComponent name="Ban" />
                  </button>
                </td>
                <td class="px-4 py-3 text-right">{{ (item.on_hand ?? 0)}}</td>
                <td class="px-4 py-3 text-right">
                  <input
                    v-if="!props.readonly"
                    :id="'quantity_' + index"
                    v-model.number="item.quantity"
                    type="number"
                    min="1"
                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                    @input="updateLineTotal(item)"
                    @keyup.enter="() => handleQuantityEnter(item, index)"
                  />
                  <span v-else>{{ item.quantity }}</span>
                </td>
                <td class="px-4 py-3 text-right">
                  <input
                    v-if="!props.readonly && props.allowChangePrice"
                    :id="'price_' + index"
                    v-model.number="item.price"
                    type="number"
                    min="1"
                    @blur="handleUnitPriceEnter(index)"
                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                    @keyup.enter="() => handleUnitPriceEnter(index)"
                  />
                  <span v-else>{{ formatNumber(item.price/ props.exchange_rate) }}</span>
                </td>
                <td class="px-4 py-3 text-right">
                  {{ formatNumber(item.sub_total / props.exchange_rate) }}
                </td>
                <td class="px-4 py-3 text-right">
                  <input
                    v-if="!props.readonly && props.allowDiscount"
                    v-model.number="item.discount_p"
                    type="number"
                    min="0"
                    max="100"
                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                    @input="updateLineTotal(item)"
                  />
                  <span v-else>{{ item.discount_p }}</span>
                </td>
                <td class="px-4 py-3 text-right">
                  <input
                    v-if="!props.readonly && props.allowDiscount"
                    v-model.number="item.discount_a"
                    type="number"
                    min="0"
                    max="100"
                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                    @keyup.enter="updateDiscount(item)"
                  />
                  <span v-else>{{ formatNumber(item.discount_a / props.exchange_rate) }}</span>
                </td>
                <td class="px-4 py-3 text-right">
                  {{ formatNumber(item.value_before_tax / props.exchange_rate) }}
                </td>
                <!--<td class="px-4 py-3 text-left">
                  <SearchSelect
                    label=""
                    v-if="!readonly"
                    v-model="item.tax_details_id"
                    :options="taxDetails"
                    @select="handleTax(item)"
                  />
                  <span v-else>{{ item.tax }}</span>
                </td>-->
                <td class="px-4 py-3 text-right relative">
                  <span
                    class="absolute top-1 right-2 text-[10px] text-gray-400"
                  >
                    {{ item.tax_rate }}%
                  </span>
                  {{ formatNumber(item.tax_amount / props.exchange_rate) }}
                </td>

                <!-- Fixed right columns -->
                <td
                  class="sticky right-0 min-w-36 bg-white dark:bg-gray-900 z-10 px-4 py-3 text-right font-medium flex items-center justify-end"
                >
                  <span>{{ formatNumber(item.net_total / props.exchange_rate) }}</span>
                  <button
                  v-if="!readonly"
                  @click="removeItem(index)"
                  class="ml-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  >
                  <IconComponent name="Trash2" />
                  </button>
                </td>
                <!--<td class="sticky right-0 z-10 bg-white dark:bg-gray-900 px-4 py-3">
                      <button
                        @click="removeItem(index)"
                        class="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        ✕
                      </button>
                    </td>-->
              </tr>
              <tr v-if="isExpanded(item)" :key="'expand-' + item.id">
                <td
                  colspan="13"
                  class="py-2 px-4 bg-white dark:bg-gray-900 relative"
                >
                  <TabComponent
                    v-model="activeTab"
                    :tabs="[
                      {
                        name: 'shelfs',
                        label: 'Shelfs',
                        visible: props.shelfManaged,
                      },
                      {
                        name: 'batch',
                        label: 'Batches',
                        visible: item.tracked_by == 'Batch',
                      },
                      {
                        name: 'serial',
                        label: 'Serials',
                        visible: item.tracked_by == 'Serial',
                      },
                    ]"
                  >
                    <template #batch>
                      <div
                        class="md:flex justify-between items-center mb-6 w-[500px]"
                      >
                        <span
                          class="text-2xl font-medium text-gray-900 dark:text-white"
                          :class="{
                            'bg-red-500 rounded-md p-2':
                              getBatchSum(item) != item.inventory_qty,
                          }"
                          >Total: {{ getBatchSum(item) }}, should be:
                          {{ item.inventory_qty }}</span
                        >
                      </div>
                      <div>
                        <table class="w-[500px]">
                          <thead class="bg-white dark:bg-gray-900">
                            <tr>
                              <th
                                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Batch No
                              </th>
                              <!--<th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Item Type</th>-->
                              <th
                                v-if="item.is_expiry == true"
                                class="px-4 w-[100px] py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Expiry Date
                              </th>
                              <th
                                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                On Hand
                              </th>
                              <th
                                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Quantity
                              </th>
                              <th class="sticky right-0 z-20 px-4 py-3"></th>
                            </tr>
                          </thead>
                          <tbody
                            class="divide-y divide-gray-200 dark:divide-gray-700"
                          >
                            <template
                              v-for="(batch, batchIndex) in item.batches"
                              :key="'batch_' + item.id"
                            >
                              <tr>
                                <td class="px-4 py-3">
                                  {{ batch.batch_no }}
                                </td>
                                <td
                                  class="px-4 py-3 w-[150px]"
                                  v-if="item.is_expiry == true"
                                >
                                  {{ formatDate(batch.expiry_date) }}
                                </td>
                                <td class="px-4 py-3 text-right">
                                  {{ batch.on_hand }}
                                </td>
                                <td class="px-4 py-3 text-right">
                                  <input
                                    @keyup.enter="
                                      handleBatchQuantityEnter(item, batchIndex)
                                    "
                                    v-if="!props.readonly"
                                    :id="
                                      'batch_quantity_' +
                                      item.items_id +
                                      '_' +
                                      batchIndex
                                    "
                                    v-model.number="batch.quantity"
                                    type="number"
                                    min="1"
                                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                                  />
                                  <span v-else>{{ batch.quantity }}</span>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                      </div>
                    </template>
                    <template #serial>
                      <div
                        class="md:flex justify-between items-center mb-6 w-[500px]"
                      >
                        <span
                          class="text-2xl font-medium text-gray-900 dark:text-white"
                          >Serials</span
                        >
                        <span
                          class="text-2xl font-medium text-gray-900 dark:text-white"
                          :class="{
                            'bg-red-500 rounded-md p-2':
                              getSerialSum(item) != item.inventory_qty,
                          }"
                          >Total: {{ getSerialSum(item) }}, should be:
                          {{ item.inventory_qty }}</span
                        >
                      </div>
                      <div>
                        <table class="w-[500px]">
                          <thead class="bg-white dark:bg-gray-900">
                            <tr>
                              <th
                                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Serial No
                              </th>
                              <!--<th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Item Type</th>-->
                              <th
                                v-if="item.is_expiry == true"
                                class="px-4 w-[100px] py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Expiry Date
                              </th>
                              <th class="sticky right-0 z-20 px-4 py-3"></th>
                            </tr>
                          </thead>
                          <tbody
                            class="divide-y divide-gray-200 dark:divide-gray-700"
                          >
                            <template
                              v-for="(serial, serialIndex) in item.serials"
                              :key="'serial_' + item.id"
                            >
                              <tr>
                                <td class="px-4 py-3">
                                  {{ serial.serial_no }}
                                </td>
                                <td
                                  class="px-4 py-3 w-[150px]"
                                  v-if="item.is_expiry == true"
                                >
                                  {{
                                    formatDate(
                                      serial.expiry_date as string | Date
                                    )
                                  }}
                                </td>
                                <td class="px-4 py-3 text-right">
                                  <input
                                    @keyup.enter="
                                      handleSerialQuantityEnter(
                                        item,
                                        serialIndex
                                      )
                                    "
                                    v-if="!props.readonly"
                                    :id="
                                      'serial_quantity_' +
                                      item.items_id +
                                      '_' +
                                      serialIndex
                                    "
                                    v-model.number="serial.quantity"
                                    :min="0"
                                    :max="1"
                                    type="number"
                                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                                  />
                                  <span v-else>{{ serial.quantity }}</span>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                      </div>
                    </template>
                    <template #shelfs>
                      <div
                        class="md:flex justify-between items-center mb-6 w-[500px]"
                      >
                        <span
                          class="text-2xl font-medium text-gray-900 dark:text-white"
                          :class="{
                            'bg-red-500 rounded-md p-2':
                              getShelfSum(item) != item.inventory_qty,
                          }"
                          >Total: {{ getShelfSum(item) }}, should be:
                          {{ item.inventory_qty }}</span
                        >
                      </div>
                      <div>
                        <table class="w-[500px]">
                          <thead class="bg-white dark:bg-gray-900">
                            <tr>
                              <th
                                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Shelf Code
                              </th>
                              <!--<th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Item Type</th>-->
                              <th
                                class="px-4 w-[100px] py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Shelf Name
                              </th>
                              <th
                                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                On Hand
                              </th>
                              <th
                                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Quantity
                              </th>
                              <th class="sticky right-0 z-20 px-4 py-3"></th>
                            </tr>
                          </thead>
                          <tbody
                            class="divide-y divide-gray-200 dark:divide-gray-700"
                          >
                            <template
                              v-for="(shelf, shelfIndex) in item.shelfs"
                              :key="'batch_' + item.id"
                            >
                              <tr>
                                <td class="px-4 py-3">
                                  {{ shelf.code }}
                                </td>
                                <td class="px-4 py-3 w-[150px]">
                                  {{ shelf.name }}
                                </td>
                                <td class="px-4 py-3 w-[150px]">
                                  {{ formatNumber(shelf.on_hand ?? 0) }}
                                </td>
                                <td class="px-4 py-3 text-right">
                                  <input
                                    @keyup.enter="
                                      handleShelfQuantityEnter(item, shelfIndex)
                                    "
                                    v-if="!props.readonly"
                                    :id="
                                      'shelf_quantity_' +
                                      item.items_id +
                                      '_' +
                                      shelfIndex
                                    "
                                    v-model.number="shelf.quantity"
                                    type="number"
                                    min="1"
                                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                                  />
                                  <span v-else>{{ shelf.quantity }}</span>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                      </div>
                    </template>
                  </TabComponent>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <template v-else>
      <ItemSearchMobile
        :allow-change-price="allowChangePrice"
        @showItemGrid="showItemGridList = true"
        :allow-discount="allowDiscount"
        :allow-new-row="allowNewRow"
        :ignore-batch-serial="ignoreBatchSerial"
        :price_lists_id="price_lists_id"
        :readonly="readonly"
        :currency="currency"
        :exchange_rate="exchange_rate"
        :shelf-managed="shelfManaged"
        :shelfs="shelfs"
        :warehouses_id="warehouses_id"
        @item="setItem"
        :tax-details="taxDetails"
        :customer="customer"
        @add-item="addItemFromMobile"
      />
      <div class="md:hidden space-y-4" id="table-records">
        <div
          v-for="(item, index) in itemList"
          :key="item.id"
          class="bg-white dark:bg-gray-900 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700"
        >
          <div class="space-y-2 text-sm">
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Code</span>
              <span class="font-medium">{{ item.code }}</span>
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Name</span>
              <span class="font-medium">{{ item.name || item.item}}</span>
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Price <span v-if="item.sales_uom_id != item.default_sales_uom_id">({{ item.sales_uom }} x {{ item.sales_uom_qty }} pcs)</span>
              <button
                @click="packingEditRowIndex = index"
                v-if="(item.packing_prices?.length ?? 0) > 0 && !readonly && mobileEditRowIndex == index"
                class="p-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                title="Edit item"
              >
                ✏️
              </button>
              <div
                v-if="packingEditRowIndex == index"
                class="mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[1000]"
              >
                <button
                  v-for="pack in item.packing_prices?.filter(
                    (e) => e.price_lists_id == props.price_lists_id
                  )"
                  @click="updatePackingPrice(item, pack)"
                  class="block w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {{ pack.unit_of_measure }} @
                  {{ formatNumber(pack.selling_price) }}
                </button>
              </div>
              <button
                v-if="!readonly && item.sales_uom_id != item.default_sales_uom_id && mobileEditRowIndex == index"
                @click="clearPackingPrice(item)"
                class="absolute bottom-1 right-1 p-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                title="Clear"
              >
                🗑️
              </button>
            </span>
              <input
                v-if="!props.readonly && props.allowChangePrice && mobileEditRowIndex == index"
                :id="'price_mobile'"
                v-model.number="item.price"
                type="number"
                min="1"
                class="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                @keyup.enter="() => handleUnitPriceEnter"
              />
              <span v-else>{{ formatNumber(item.price / props.exchange_rate) }}</span>
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Quantity</span>
              <input
                v-if="!props.readonly && mobileEditRowIndex == index"
                :id="'quantity_mobile'"
                v-model.number="item.quantity"
                type="number"
                min="1"
                class="px-3 py-2 max-w-[120-px] w-[120px] rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                @input="updateLineTotal(item)"
                @keyup.enter="() => handleQuantityEnter"
              />
              <span v-else class="font-medium">{{ item.quantity }}</span>
            </div>
            <div key="mobile-code" class="flex justify-between" v-if="!props.readonly && mobileEditRowIndex == index">
              <span class="text-gray-500 dark:text-gray-400">Discount %</span>
              <input
                v-model.number="item.discount_p"
                type="number"
                min="0"
                max="100"
                class="px-3 py-2 max-w[120px] min-w-[120px] rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                @keyup.enter="updateDiscount(item)"
              />
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Discount</span>
              <input
                v-if="!props.readonly && mobileEditRowIndex == index"
                v-model.number="item.discount_a"
                type="number"
                min="0"
                max="100"
                class="px-3 py-2 max-w[120px] min-w-[120px] rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                @keyup.enter="updateDiscount(item)"
              />
              <span class="font-medium" v-else>{{ formatNumber(item.discount_a  / props.exchange_rate) }}</span>
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Tax Amount ({{ item.tax }})</span>
              <span class="font-medium">{{ formatNumber(item.tax_amount / props.exchange_rate) }}</span>
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Total</span>
              <span class="font-medium">{{ formatNumber(item.net_total / props.exchange_rate) }}</span>
            </div>
          </div>
          <div
            class="mt-4 flex justify-end space-x-2 border-t dark:border-gray-700 pt-4"
            id="sales-documents-actions"
            v-if="!readonly"
          >
          <button
              :key="'edit-item=' + item.id"
              v-if="mobileEditRowIndex != index"
              @click="mobileEditRowIndex = index"
              class="[ 'p-2', 'text-warning-600 hover:text-warning-700', 'dark:text-warning-400 dark:hover:text-warning-300' ]"
              title="Edit Item"
            >
              <IconComponent name="Pencil" />
            </button>
            <button
              :key="'edit-item=' + item.id"
              v-if="mobileEditRowIndex == index"
              @click="mobileEditRowIndex = -1"
              class="[ 'p-2', 'text-warning-600 hover:text-warning-700', 'dark:text-warning-400 dark:hover:text-warning-300' ]"
              title="Edit Item"
            >
              <IconComponent name="Save" />
            </button>
            <button
              :key="'remove-item=' + item.id"
              @click="itemList.splice(index, 1)"
              class="[ 'p-2', 'text-warning-600 hover:text-warning-700', 'dark:text-warning-400 dark:hover:text-warning-300' ]"
              title="Remove Item"
            >
              <IconComponent name="Trash2" />
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Totals -->
    <div class="mt-4 flex justify-end">
      <div class="w-72 space-y-2">
          <div class="flex justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">Item Total</span>
          <span class="font-medium">{{ formatNumber(subtotal / props.exchange_rate) }}</span>
        </div>
        <div class="flex justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">Discount</span>
          <span class="font-medium">{{ formatNumber(discount / props.exchange_rate) }}</span>
        </div>
        <div class="flex justify-between text-sm" v-if="(outlet?.service_charge ?? 0) > 0">
          <span class="text-gray-600 dark:text-gray-400">Service Charge ({{ formatNumber(outlet?.service_charge ?? 0) }}%)</span>
          <span class="font-medium">{{ formatNumber(service_charge / props.exchange_rate) }}</span>
        </div>
        <div class="flex justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">Value For Tax</span>
          <span class="font-medium">{{ formatNumber(value_before_tax / props.exchange_rate) }}</span>
        </div>
        <div class="flex justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">Tax</span>
          <span class="font-medium">{{ formatNumber(taxAmount / props.exchange_rate) }}</span>
        </div>
        <div
          class="flex justify-between text-lg font-medium pt-2 border-t border-gray-200 dark:border-gray-700"
        >
          <span>Total</span>
          <span>{{ formatNumber(total / props.exchange_rate) }}</span>
        </div>
      </div>
    </div>

    <ItemGridSelectItems
      v-if="showItemsDialog"
      :items="selectItems"
      :warehouses_id="warehouses_id"
      :price_lists_id="price_lists_id"
      @close="closeItemSelect"
      @select="addItemFromMobile"
    />

    <ItemSelectGrid
      v-if="showItemGridList"
      :warehouses_id="props.warehouses_id"
      :price_lists_id="props.price_lists_id"
      @close="showItemGridList = false"
      @selected="handleSelectedItems"
    />

  </div>
</template>
